import { motion, useReducedMotion } from "framer-motion";
import { Btn, Eyebrow } from "@/components/primitives";
import { brand, cta, trustChips } from "@/content/site";
import GrowthRing from "@/components/visuals/GrowthRing";
import { track } from "@/lib/analytics";
import { ArrowRight } from "lucide-react";

const heroImage =
  "https://images.unsplash.com/photo-1549923746-c502d488b3ea?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";

const headlineLines = ["Great trades", "deserve to be", "found."];
const flow = ["Visibility", "Enquiry", "Follow-up", "Booked Work"];

const easing = [0.16, 1, 0.3, 1];

export default function Hero() {
  const reduce = useReducedMotion();

  const lineVariants = {
    hidden: { y: "110%" },
    show: (i) => ({
      y: "0%",
      transition: { duration: 0.85, ease: easing, delay: 0.15 + i * 0.12 },
    }),
  };

  return (
    <section
      data-testid="hero-section"
      className="relative bg-white pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* soft background wash */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(1200px 600px at 85% -10%, rgba(44,213,156,0.10), transparent 60%), radial-gradient(900px 500px at 0% 30%, rgba(141,254,63,0.08), transparent 55%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left */}
          <div className="lg:col-span-7">
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={reduce ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Eyebrow>{brand.tagline}</Eyebrow>
            </motion.div>

            <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-forest">
              {headlineLines.map((line, i) => (
                <span
                  key={i}
                  className="block overflow-hidden"
                  style={{ paddingBottom: "0.05em" }}
                >
                  {reduce ? (
                    <span className="block">{line}</span>
                  ) : (
                    <motion.span
                      className="block"
                      custom={i}
                      variants={lineVariants}
                      initial="hidden"
                      animate="show"
                    >
                      {i === 2 ? (
                        <span className="text-mint">{line}</span>
                      ) : (
                        line
                      )}
                    </motion.span>
                  )}
                </span>
              ))}
            </h1>

            <motion.p
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={reduce ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="mt-7 max-w-xl text-lg md:text-xl text-mid-neutral leading-relaxed"
            >
              Green Circle Group helps Australian trade and home-service businesses
              get found, generate more enquiries and turn more opportunities
              into booked work—with a connected digital growth system working in
              the background.
            </motion.p>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={reduce ? false : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.72 }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Btn
                to={cta.primary.to}
                size="lg"
                data-testid="hero-primary-cta"
                onClick={() => track("growth_assessment_started", { source: "hero" })}
              >
                {cta.primary.label}
              </Btn>
              <Btn
                to={cta.secondary.to}
                variant="secondary"
                size="lg"
                withArrow
                data-testid="hero-secondary-cta"
              >
                {cta.secondary.label}
              </Btn>
            </motion.div>

            <motion.ul
              initial={reduce ? false : { opacity: 0 }}
              animate={reduce ? false : { opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-10 flex flex-wrap gap-2.5"
            >
              {trustChips.map((chip) => (
                <li
                  key={chip}
                  data-testid="hero-trust-chip"
                  className="inline-flex items-center gap-2 rounded-full bg-soft border border-border-neutral px-4 py-2 text-sm font-semibold text-forest"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-mint" />
                  {chip}
                </li>
              ))}
            </motion.ul>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5">
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.95 }}
              animate={reduce ? false : { opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.4, ease: easing }}
              className="relative mx-auto max-w-md lg:max-w-none"
            >
              <div className="absolute inset-0 flex items-center justify-center -z-0">
                <GrowthRing size={520} className="opacity-90" duration={70} />
              </div>
              <div className="relative aspect-[4/5] rounded-[3rem] overflow-hidden border-4 border-white shadow-[0_30px_80px_rgba(3,34,26,0.18)]">
                <img
                  src={heroImage}
                  alt="Australian tradesperson shaking hands with a smiling customer after a job well done"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/40 via-transparent to-transparent" />
              </div>

              {/* floating connected-flow chip */}
              <motion.div
                initial={reduce ? false : { opacity: 0, y: 20 }}
                animate={reduce ? false : { opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1 }}
                className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[90%]"
              >
                <div className="rounded-2xl bg-white/95 backdrop-blur border border-border-neutral px-4 py-3 shadow-[0_16px_40px_rgba(3,34,26,0.12)]">
                  <div className="flex items-center justify-between text-[11px] sm:text-xs font-bold text-forest">
                    {flow.map((f, i) => (
                      <span key={f} className="flex items-center gap-1.5">
                        {f}
                        {i < flow.length - 1 && (
                          <ArrowRight className="h-3 w-3 text-mint" />
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
